import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { CustomButton } from "../../utils/CustomTagComponents";
import { useSelector } from "react-redux";
import {
  getForexExchangeData,
  getForexServiceData,
  submitForex,
} from "../../services/ForexServicePageService";
import LoginQRModal from "../../components/LoginQRModal ";

const cardSx = {
  borderRadius: "16px",
  border: "0.5px solid #FF9500",
  backgroundColor: "#002045",
  p: 3,
};

const fieldSx = {
  backgroundColor: "#FFF4E6",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "10px",
  },
};

const smallSelectSx = {
  minWidth: 84,
  height: 40,
  borderRadius: "10px",
  backgroundColor: "#FF9500",
  color: "#000",
  "& .MuiSelect-icon": { color: "#000" },
  "& fieldset": { border: "none" },
  px: 1,
};

const Conversation = ({about_short_desc}) => {
  const { auth } = useSelector((state) => state.auth || {});
  const isLoggedIn = Boolean(auth?.idToken || auth?.localId || auth?.email);

  const { country_id, country_name } = useSelector((state) => state.auth || {});

  const [formData, setFormData] = useState({
    city: "", // "Select City" text field
    transfer_from: "INR", // sendCur (e.g. "USD")
    transfer_to: "USD", // recvCur (e.g. "INR")
    forex_service: "", // service type (e.g. "Uni. Fee Transfer")
    sending_amount: "1", // sendAmt
    receiving_amount: "", // calculated or manually entered
    total_amount: "", // totalAmount shown below
  });
  // Converter state

  const [errors, setErrors] = useState({});

  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [liveRates, setLiveRates] = useState({});
  const [liveRate, setLiveRate] = useState(null);
  const [rateTimestamp, setRateTimestamp] = useState(null);
  const [leftCur, setLeftCur] = useState("INR");
  const [rightCur, setRightCur] = useState("USD");
  const [leftAmt, setLeftAmt] = useState("1");
  const [rightAmt, setRightAmt] = useState("");

  const [loading, setLoading] = useState(true);
  const [forexServiceData, setForexServiceData] = useState(null);
  const [cityInput, setCityInput] = useState("");

  useEffect(() => {
    if (!country_id) return; // Wait for Redux country_id
    setLoading(true);

    const fetchFinanceSupportData = async () => {
      try {
        const res = await getForexServiceData(country_id);
        if (res?.data?.success && res?.data?.data) {
          setForexServiceData(res.data.data);
        } else {
          setForexServiceData([]);
        }
      } catch (err) {
        console.error("❌ Error fetching Finance Support page:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFinanceSupportData();
  }, [country_id]);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const res = await getForexExchangeData();

        if (res?.data?.success) {
          setCurrencies(res.data?.currencies);
          setLiveRates(res.data?.rateMap);
          let rateMap = res.data?.rateMap || {};
          setRateTimestamp(res.data?.timestamp || null);

          // ✅ Default: 1 INR = USD
          const defaultPair = "USDINR";
          const defaultRate = rateMap[defaultPair] || null;
          setLiveRate(defaultRate);

          if (defaultRate) {
            const usdAmt = (1 / defaultRate).toFixed(2);
            setRightAmt(usdAmt);
            setFormData((prev) => ({
              ...prev,
              receiving_amount: usdAmt,
              total_amount: "1.00",
            }));
          }
        } else {
          setCurrencies([]);
          setLiveRates({});
          setRateTimestamp(null);
        }
      } catch (err) {
        console.error("Error fetching IBR rates:", err);
      }
    };
    fetchRates();
  }, []);

  // ✅ Update live rate when currencies change
  useEffect(() => {
    if (!liveRates || !leftCur || !rightCur) return;

    if (leftCur === rightCur) {
      setLiveRate(1);
      return;
    }

    // Try to find direct or reverse pair (e.g. USDINR or INRUSD)
    const directPair = `${leftCur}${rightCur}`;
    const reversePair = `${rightCur}${leftCur}`;

    let rate = null;

    if (liveRates[directPair]) {
      rate = liveRates[directPair];
    } else if (liveRates[reversePair]) {
      rate = 1 / liveRates[reversePair];
    }

    if (rate) setLiveRate(rate);
  }, [leftCur, rightCur, liveRates]);

  useEffect(() => {
    if (!liveRate) return;

    const leftValue = parseFloat(leftAmt);
    const rightValue = parseFloat(rightAmt);

    // If user typed in left side, sync right side
    if (leftValue && !isNaN(leftValue)) {
      setRightAmt((leftValue * liveRate).toFixed(2));
    }
    // Or if user typed in right side previously, sync left side
    else if (rightValue && !isNaN(rightValue)) {
      setLeftAmt((rightValue / liveRate).toFixed(2));
    }
  }, [liveRate, leftCur, rightCur]);

  useEffect(() => {
    if (
      !liveRates ||
      !formData?.transfer_from ||
      !formData?.transfer_to ||
      !formData?.sending_amount
    )
      return;

    const from = formData.transfer_from;
    const to = formData.transfer_to;
    const sending = parseFloat(formData.sending_amount);

    if (isNaN(sending)) return;

    // const serviceChargeRate = 0.02; // 2% charge
    const serviceChargeRate = 0;
    let rate = null;

    // Handle same currency
    if (from === to) {
      rate = 1;
    } else {
      const directPair = `${from}${to}`;
      const reversePair = `${to}${from}`;

      if (liveRates[directPair]) {
        rate = parseFloat(liveRates[directPair]);
      } else if (liveRates[reversePair]) {
        rate = 1 / parseFloat(liveRates[reversePair]);
      }
    }

    if (rate) {
      // ✅ Step 1: Calculate receiving amount
      const receiving = sending * rate;

      // ✅ Step 2: Apply service charge on receiving amount
      const serviceCharge = receiving * serviceChargeRate;

      // ✅ Step 3: Deduct service charge to get total amount
      const totalAfterCharge = receiving - serviceCharge;

      setFormData((prev) => ({
        ...prev,
        rate: rate.toFixed(6),
        receiving_amount: receiving.toFixed(2), // full received before charge
        service_charge: serviceCharge.toFixed(2), // 2% of receiving
        total_amount: totalAfterCharge.toFixed(2), // after deducting charge
      }));
    }
  }, [
    formData?.sending_amount,
    formData?.transfer_from,
    formData?.transfer_to,
    liveRates,
  ]);

  // ✅ Conversion Handlers
  const handleLeftAmt = (v) => {
    setLeftAmt(v);
    const n = parseFloat(v);
    if (Number.isFinite(n) && liveRate) {
      setRightAmt((n * liveRate).toFixed(2));
    } else setRightAmt("");
  };

  const handleRightAmt = (v) => {
    setRightAmt(v);
    const n = parseFloat(v);
    if (Number.isFinite(n) && liveRate) {
      setLeftAmt((n / liveRate).toFixed(2));
    } else setLeftAmt("");
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.city.trim()) newErrors.city = "City / Address is required";
    if (!formData.transfer_from)
      newErrors.transfer_from = "Select source currency";
    if (!formData.transfer_to)
      newErrors.transfer_to = "Select destination currency";
    if (!formData.forex_service)
      newErrors.forex_service = "Please choose a service";
    if (!formData.sending_amount)
      newErrors.sending_amount = "Enter sending amount";
    if (!formData.receiving_amount)
      newErrors.receiving_amount = "Enter receiving amount";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isLoggedIn) {
      setLoginModalOpen(true);
      return;
    }
    if (!validate()) return;
    try {
      setLoading(true);
      const payload = {
        address: formData.city,
        transfer_from_currency: formData.transfer_from,
        transfer_to_currency: formData.transfer_to,
        forex_service_id: formData.forex_service,
        sending_amount: formData.sending_amount,
        receving_amount: formData.receiving_amount,
        rate: formData.rate,
        total_amount: formData.total_amount,
      };

      const res = await submitForex(payload);

      if (res?.data?.success) {
        alert("✅ Order created successfully!");

        // ✅ Reset form
        setFormData({
          city: "",
          transfer_from: "INR",
          transfer_to: "USD",
          forex_service: "",
          sending_amount: "1",
          receiving_amount: "",
          total_amount: "",
        });
        setCityInput("");
        setLeftAmt("1");
        setRightAmt(""); // reset converted amount
      } else {
        alert(res?.data?.message || "❌ Failed to create order.");
      }
    } catch (err) {
      console.error("❌ Error creating order:", err);
      alert("❌ Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress color="warning" />
      </Box>
    );

  return (
    <Box sx={{ background: "#fff" }}>
      <Container sx={{
        py: { xs: 2, sm: 2, md: 3 },
        px: { xs: 3, sm: 4, md: 6 },
        paddingLeft: { xs: "24px !important", md: "48px !important" },
        paddingRight: { xs: "24px !important", md: "48px !important" }
      }}>
        <Grid container spacing={4}>
          {/* Left: Converter */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={0}
              sx={{
                ...cardSx,
                borderRadius: "20px",
                border: "0.5px solid #FF9500",
                backgroundColor: "#F8F8F8",
                p: { xs: "20px", md: "25px 25px 25px 35px" },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 12, md: 15 },
                  mb: "25px",
                  color: "#000",
                }}
              >
                Currency Converter With Live Rate
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  borderRadius: "20px",
                  border: "none",
                  backgroundColor: "#002045",
                  p: { xs: "10px 15px", md: "11px 20px" },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 11,
                    color: "#ffffff",
                    mb: 2,
                    fontStyle: "bold",
                  }}
                >
                  EUR-INR Currency Converter
                </Typography>

                {/* Responsive input box layout */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    mt: 2,
                  }}
                >
                  {/* Left side */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                      backgroundColor: "#FFF4E6",
                      border: "1.5px solid #FF9500",
                      borderRadius: "4px",
                      overflow: "hidden",
                      width: "100%",
                      p: "8px 11px",
                      height: "63px",
                    }}
                  >
                    <TextField
                      variant="outlined"
                      placeholder={`Enter ${leftCur} Amount`}
                      value={leftAmt}
                      onChange={(e) => handleLeftAmt(e.target.value)}
                      fullWidth
                      inputProps={{
                        inputMode: "decimal",
                        style: {
                          padding: "0px",
                          fontWeight: 600,
                          fontSize: "13px",
                          height: "41px",
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          border: "none",
                          "& fieldset": { border: "none" },
                          "&:hover fieldset": { border: "none" },
                          "&.Mui-focused fieldset": { border: "none" },
                        },
                        "& input::placeholder": {
                          color: "#999",
                          opacity: 1,
                          fontSize: "13px",
                          fontWeight: 500,
                        },
                      }}
                    />

                    <Select
                      value={leftCur}
                      onChange={(e) => setLeftCur(e.target.value)}
                      IconComponent={KeyboardArrowDownIcon}
                      sx={{
                        backgroundColor: "#fd0000",
                        color: "#ffffff",
                        fontWeight: 700,
                        fontSize: "11px",
                        p: "5px 10px",
                        height: "100%",
                        minWidth: "63px",
                        maxWidth: "63px",
                        borderRadius: "2px",
                        borderLeft: "1.5px solid #FF9500",
                        "& .MuiSelect-select": {
                          display: "flex",
                          alignItems: "center",
                          padding: "0px 28px 0px 0px !important",
                        },
                        "& .MuiSelect-icon": {
                          color: "#ffffff",
                          right: 8,
                        },
                        "& fieldset": { border: "none" },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: "#ffffff",

                            /* All items */
                            "& .MuiMenuItem-root": {
                              color: "#002045",
                              fontWeight: 600,
                              fontSize: "11px",
                            },

                            /* Hover */
                            "& .MuiMenuItem-root:hover": {
                              backgroundColor: "rgba(255,255,255,0.2)",
                            },

                            /* Selected item */
                            "& .MuiMenuItem-root.Mui-selected": {
                              backgroundColor: "#fd0000 !important",
                              color: "#ffffff !important",
                            },

                            "& .MuiMenuItem-root.Mui-selected:hover": {
                              backgroundColor: "#fd0000",
                            },
                          },
                        },
                      }}
                    >
                      {currencies.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>

                  {/* Right side */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                      backgroundColor: "#FFF4E6",
                      border: "1.5px solid #FF9500",
                      borderRadius: "4px",
                      overflow: "hidden",
                      width: "100%",
                      p: "8px 11px",
                      height: "63px",
                    }}
                  >
                    <TextField
                      variant="outlined"
                      placeholder={`Enter ${rightCur} Amount`}
                      value={rightAmt}
                      onChange={(e) => handleRightAmt(e.target.value)}
                      fullWidth
                      inputProps={{
                        inputMode: "decimal",
                        style: {
                          padding: "0px",
                          fontWeight: 700,
                          fontSize: "13px",
                          height: "41px",
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          border: "none",
                          "& fieldset": { border: "none" },
                          "&:hover fieldset": { border: "none" },
                          "&.Mui-focused fieldset": { border: "none" },
                        },
                        "& input::placeholder": {
                          color: "#999",
                          opacity: 1,
                          fontSize: "13px",
                          fontWeight: 500,
                        },
                      }}
                    />

                    <Select
                      value={rightCur}
                      onChange={(e) => setRightCur(e.target.value)}
                      IconComponent={KeyboardArrowDownIcon}
                      sx={{
                        backgroundColor: "#fd0000",
                        color: "#ffffff",
                        fontWeight: 700,
                        fontSize: "11px",
                        p: "5px 10px",
                        height: "100%",
                        minWidth: "63px",
                        maxWidth: "63px",
                        borderRadius: "2px",
                        borderLeft: "1.5px solid #FF9500",
                        "& .MuiSelect-select": {
                          padding: "0px 28px 0px 0px !important",
                          display: "flex",
                          alignItems: "center",
                        },
                        "& .MuiSelect-icon": {
                          color: "#ffffff",
                          right: 8,
                        },
                        "& fieldset": { border: "none" },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: "#ffffff",

                            /* All items */
                            "& .MuiMenuItem-root": {
                              color: "#002045",
                              fontWeight: 600,
                              fontSize: "11px",
                            },

                            /* Hover */
                            "& .MuiMenuItem-root:hover": {
                              backgroundColor: "rgba(255,255,255,0.2)",
                            },

                            /* Selected item */
                            "& .MuiMenuItem-root.Mui-selected": {
                              backgroundColor: "#fd0000 !important",
                              color: "#ffffff !important",
                            },

                            "& .MuiMenuItem-root.Mui-selected:hover": {
                              backgroundColor: "#fd0000",
                            },
                          },
                        },
                      }}
                    >
                      {currencies.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                </Box>

                {/* Rate Info Section */}
                <Stack
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    mt: { xs: "30px", md: "46px" },
                    gap: "8px",
                    textAlign: "center",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      gap: "11px",
                      flexWrap: "wrap",
                    }}
                  >
                    <Box
                      sx={{
                        width: 13,
                        height: 13,
                        borderRadius: "50%",
                        bgcolor: "#15FF00",
                      }}
                    />
                    <Typography
                      sx={{ fontWeight: 700, fontSize: { xs: 15, md: 18 }, color: "#ffffff" }}
                    >
                      1 {leftCur} = {liveRate ? liveRate.toFixed(4) : "--"}{" "}
                      {rightCur}
                    </Typography>
                  </Stack>

                  <Typography
                    sx={{
                      fontSize: { xs: 11, md: 12 },
                      color: "#ffffff",
                      // letterSpacing: 0.3,
                    }}
                  >
                    Interbank/ Mid-Market Rate At: {rateTimestamp}
                  </Typography>
                </Stack>
              </Paper>
            </Paper>

             <Box mt={3} sx={{
                ...cardSx,
                borderRadius: "20px",
                border: "0.5px solid #FF9500",
                backgroundColor: "#F8F8F8",
                p: { xs: "20px", md: "25px 25px 25px 35px" },
              }}>
             <Typography
                        sx={{ textAlign: "justify", color: "#000" }}
                        dangerouslySetInnerHTML={{ __html: about_short_desc }}
                      />
          </Box>

          </Grid>

          {/* Right: Booking form */}
          <Grid size={{ xs: 12, md: 6 }}>
            <form onSubmit={handleSubmit} noValidate>
              <Stack
                direction="column"
                sx={{
                  gap: "10px",
                }}
              >
                <Stack direction="column">
                  <Paper
                    elevation={0}
                    sx={{ ...cardSx, p: "0px", borderRadius: "10px" }}
                  >
                    <TextField
                      placeholder="Enter City / Address"
                      fullWidth
                      variant="outlined"
                      value={cityInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCityInput(value);
                        setFormData((prev) => ({
                          ...prev,
                          city: value,
                        }));
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#FFF6EE",
                          borderRadius: "10px",
                          fontSize: "13px",
                          fontWeight: 700,
                          height: "41px",
                          "& fieldset": { border: "none" },
                          "&:hover fieldset": { border: "none" },
                          "&.Mui-focused fieldset": { border: "none" },
                        },
                        "& .MuiInputBase-input::placeholder": {
                          fontWeight: 600,
                          fontSize: "13px",
                          color: "#000",
                          opacity: 1,
                        },
                      }}
                    />
                  </Paper>
                  {errors.city && (
                    <Typography color="error" fontSize={12}>
                      {errors.city}
                    </Typography>
                  )}
                </Stack>

                <Paper
                  variant="outlined"
                  sx={{
                    ...cardSx,
                    borderRadius: "20px",
                    p: "17px 17px 41px 17px",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography
                        sx={{
                          mb: 1,
                          fontSize: 13,
                          fontWeight: 700,
                          fontStyle: "bold",
                          color: "#ffffff"
                        }}
                      >
                        Transfer From
                      </Typography>
                      <Select
                        fullWidth
                        value={formData.transfer_from}
                        onChange={(e) =>
                          handleChange("transfer_from", e.target.value)
                        }
                        IconComponent={KeyboardArrowDownIcon}
                        sx={{
                          ...fieldSx,
                          "& .MuiSelect-select": {
                            padding: "0 !important", // ✅ removes default internal padding
                            display: "flex",
                            alignItems: "center",
                          },
                          "& fieldset": {
                            border: "1px solid #FF9500", // default border
                          },
                          "&:hover fieldset": {
                            border: "1px solid #FF9500", // keep orange on hover
                          },
                          "&.Mui-focused fieldset": {
                            border: "1px solid #FF9500", // keep orange on focus
                          },
                          "& .MuiSelect-icon": {
                            color: "#000",
                            right: 8,
                          },
                          p: "5px 16px",
                          height: "32px",
                          borderRadius: "4px",
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: "#ffffff",

                              /* All items */
                              "& .MuiMenuItem-root": {
                                color: "#002045",
                                fontWeight: 600,
                                fontSize: "11px",
                              },

                              /* Hover */
                              "& .MuiMenuItem-root:hover": {
                                backgroundColor: "rgba(255,255,255,0.2)",
                              },

                              /* Selected item */
                              "& .MuiMenuItem-root.Mui-selected": {
                                backgroundColor: "#fd0000 !important",
                                color: "#ffffff !important",
                              },

                              "& .MuiMenuItem-root.Mui-selected:hover": {
                                backgroundColor: "#fd0000",
                              },
                            },
                          },
                        }}
                      >
                        {currencies.map((c) => (
                          <MenuItem key={c} value={c}>
                            {c}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.transfer_from && (
                        <Typography color="error" fontSize={12}>
                          {errors.transfer_from}
                        </Typography>
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography
                        sx={{
                          mb: 1,
                          fontSize: 13,
                          fontWeight: 700,
                          fontStyle: "bold",
                          color: "#ffffff"
                        }}
                      >
                        Transfer To
                      </Typography>
                      <Select
                        fullWidth
                        value={formData.transfer_to}
                        onChange={(e) =>
                          handleChange("transfer_to", e.target.value)
                        }
                        IconComponent={KeyboardArrowDownIcon}
                        sx={{
                          ...fieldSx,
                          "& .MuiSelect-select": {
                            padding: "0 !important", // ✅ removes default internal padding
                            display: "flex",
                            alignItems: "center",
                          },
                          "& fieldset": {
                            border: "1px solid #FF9500", // default border
                          },
                          "&:hover fieldset": {
                            border: "1px solid #FF9500", // keep orange on hover
                          },
                          "&.Mui-focused fieldset": {
                            border: "1px solid #FF9500", // keep orange on focus
                          },
                          "& .MuiSelect-icon": {
                            color: "#000",
                            right: 8,
                          },
                          p: "5px 16px",
                          height: "32px",
                          borderRadius: "4px",
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: "#ffffff",

                              /* All items */
                              "& .MuiMenuItem-root": {
                                color: "#002045",
                                fontWeight: 600,
                                fontSize: "11px",
                              },

                              /* Hover */
                              "& .MuiMenuItem-root:hover": {
                                backgroundColor: "rgba(255,255,255,0.2)",
                              },

                              /* Selected item */
                              "& .MuiMenuItem-root.Mui-selected": {
                                backgroundColor: "#fd0000 !important",
                                color: "#ffffff !important",
                              },

                              "& .MuiMenuItem-root.Mui-selected:hover": {
                                backgroundColor: "#fd0000",
                              },
                            },
                          },
                        }}
                      >
                        {
                          currencies.map((c) => (
                            <MenuItem key={c} value={c}>
                              {c}
                            </MenuItem>
                          ))
                        }
                      </Select>
                      {errors.transfer_to && (
                        <Typography color="error" fontSize={12}>
                          {errors.transfer_to}
                        </Typography>
                      )}
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Typography
                        sx={{
                          mb: 1,
                          fontSize: 13,
                          fontWeight: 700,
                          fontStyle: "bold",
                          color: "#ffffff"
                        }}
                      >
                        Forex Service
                      </Typography>
                      <Select
                        fullWidth
                        value={formData.forex_service}
                        onChange={(e) =>
                          handleChange("forex_service", e.target.value)
                        }
                        IconComponent={KeyboardArrowDownIcon}
                        sx={{
                          ...fieldSx,
                          "& .MuiSelect-select": {
                            padding: "0 !important", // ✅ removes default internal padding
                            display: "flex",
                            alignItems: "center",
                          },
                          "& fieldset": {
                            border: "1px solid #FF9500", // default border
                          },
                          "&:hover fieldset": {
                            border: "1px solid #FF9500", // keep orange on hover
                          },
                          "&.Mui-focused fieldset": {
                            border: "1px solid #FF9500", // keep orange on focus
                          },
                          "& .MuiSelect-icon": {
                            color: "#000",
                            right: 8,
                          },
                          p: "5px 16px",
                          height: "32px",
                          borderRadius: "4px",
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: "#ffffff",

                              /* All items */
                              "& .MuiMenuItem-root": {
                                color: "#002045",
                                fontWeight: 600,
                                fontSize: "11px",
                              },

                              /* Hover */
                              "& .MuiMenuItem-root:hover": {
                                backgroundColor: "rgba(255,255,255,0.2)",
                              },

                              /* Selected item */
                              "& .MuiMenuItem-root.Mui-selected": {
                                backgroundColor: "#fd0000 !important",
                                color: "#ffffff !important",
                              },

                              "& .MuiMenuItem-root.Mui-selected:hover": {
                                backgroundColor: "#fd0000",
                              },
                            },
                          },
                        }}
                      >
                        {forexServiceData?.length > 0 &&
                          forexServiceData.map((service, index) => (
                            <MenuItem value={service.id} key={index}>
                              {service.name}
                            </MenuItem>
                          ))}
                      </Select>
                      {errors.forex_service && (
                        <Typography color="error" fontSize={12}>
                          {errors.forex_service}
                        </Typography>
                      )}
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Typography
                        sx={{
                          mb: 1,
                          fontSize: 13,
                          fontWeight: 700,
                          fontStyle: "bold",
                          color: "#ffffff"
                        }}
                      >
                        Sending Amount
                      </Typography>
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                          backgroundColor: "#FFF4E6",
                          border: "1px solid #FF9500",
                          borderRadius: "4px",
                          overflow: "hidden",
                          p: "8px 11px",
                          width: "100%",
                          height: "41px",
                          transition: "border-color 0.3s",
                          "&:hover": { borderColor: "#FF9500" },
                          "&:focus-within": { borderColor: "#FF9500" },
                        }}
                      >
                        {/* Left: Currency Select */}
                        <Select
                          value={formData.transfer_from}
                          onChange={(e) =>
                            handleChange("transfer_from", e.target.value)
                          }
                          IconComponent={KeyboardArrowDownIcon}
                          sx={{
                            backgroundColor: "#fd0000",
                            color: "#ffffff",
                            fontWeight: 600,
                            fontSize: "13px",
                            px: 2,
                            height: "100%",
                            borderRadius: "0px",
                            display: "flex",
                            alignItems: "center",
                            minWidth: { xs: "90px", sm: "118px" },
                            maxWidth: { xs: "90px", sm: "118px" },
                            "& .MuiSelect-select": {
                              padding: "0px 24px 0px 0px !important",
                              display: "flex",
                              alignItems: "center",
                            },
                            "& .MuiSelect-icon": {
                              color: "#fff",
                              right: 8,
                            },
                            "& fieldset": { border: "none" },
                            "&:hover fieldset": { border: "none" },
                            "&.Mui-focused fieldset": { border: "none" },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                bgcolor: "#ffffff",

                                /* All items */
                                "& .MuiMenuItem-root": {
                                  color: "#002045",
                                  fontWeight: 600,
                                  fontSize: "11px",
                                },

                                /* Hover */
                                "& .MuiMenuItem-root:hover": {
                                  backgroundColor: "rgba(255,255,255,0.2)",
                                },

                                /* Selected item */
                                "& .MuiMenuItem-root.Mui-selected": {
                                  backgroundColor: "#fd0000 !important",
                                  color: "#ffffff !important",
                                },

                                "& .MuiMenuItem-root.Mui-selected:hover": {
                                  backgroundColor: "#fd0000",
                                },
                              },
                            },
                          }}
                        >
                          {currencies.map((c) => (
                            <MenuItem key={c} value={c}>
                              {c}
                            </MenuItem>
                          ))}
                        </Select>

                        {/* Right: Amount Input */}
                        <TextField
                          variant="outlined"
                          placeholder="Sending Amount"
                          value={formData.sending_amount}
                          onChange={(e) =>
                            handleChange("sending_amount", e.target.value)
                          }
                          fullWidth
                          inputProps={{
                            inputMode: "decimal",
                            style: {
                              padding: "0px",
                              fontWeight: 600,
                              fontSize: "13px",
                              textAlign: "right",
                            },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              border: "none",
                              "& fieldset": { border: "none" },
                              "&:hover fieldset": { border: "none" },
                              "&.Mui-focused fieldset": { border: "none" },
                            },
                            "& input::placeholder": {
                              color: "#7A7A7A",
                              opacity: 1,
                              fontSize: "13px",
                              fontWeight: 600,
                            },
                            pr: { xs: 0, md: 2 },
                          }}
                        />
                      </Stack>
                      {errors.sending_amount && (
                        <Typography color="error" fontSize={12}>
                          {errors.sending_amount}
                        </Typography>
                      )}
                    </Grid>

                    {/* Receiving Amount */}
                    <Grid size={{ xs: 12 }}>
                      <Typography
                        sx={{
                          mb: 1,
                          fontSize: 13,
                          fontWeight: 700,
                          fontStyle: "bold",
                          color: "#ffffff"
                        }}
                      >
                        Receiving Amount
                      </Typography>
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                          backgroundColor: "#FFF4E6",
                          border: "1px solid #FF9500",
                          borderRadius: "4px",
                          overflow: "hidden",
                          p: "8px 11px",
                          width: "100%",
                          height: "41px",
                          transition: "border-color 0.3s",
                          "&:hover": { borderColor: "#FF9500" },
                          "&:focus-within": { borderColor: "#FF9500" },
                        }}
                      >
                        {/* Left: Currency Select */}
                        <Select
                          value={formData.transfer_to}
                          onChange={(e) =>
                            handleChange("transfer_to", e.target.value)
                          }
                          IconComponent={KeyboardArrowDownIcon}
                          sx={{
                            backgroundColor: "#fd0000",
                            color: "#ffffff",
                            fontWeight: 600,
                            fontSize: "13px",
                            px: 2,
                            height: "100%",
                            borderRadius: "0px",
                            display: "flex",
                            alignItems: "center",
                            minWidth: { xs: "90px", sm: "118px" },
                            maxWidth: { xs: "90px", sm: "118px" },
                            "& .MuiSelect-select": {
                              padding: "0px 24px 0px 0px !important",
                              display: "flex",
                              alignItems: "center",
                            },
                            "& .MuiSelect-icon": {
                              color: "#fff",
                              right: 8,
                            },
                            "& fieldset": { border: "none" },
                            "&:hover fieldset": { border: "none" },
                            "&.Mui-focused fieldset": { border: "none" },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                bgcolor: "#ffffff",

                                /* All items */
                                "& .MuiMenuItem-root": {
                                  color: "#002045",
                                  fontWeight: 600,
                                  fontSize: "11px",
                                },

                                /* Hover */
                                "& .MuiMenuItem-root:hover": {
                                  backgroundColor: "rgba(255,255,255,0.2)",
                                },

                                /* Selected item */
                                "& .MuiMenuItem-root.Mui-selected": {
                                  backgroundColor: "#fd0000 !important",
                                  color: "#ffffff !important",
                                },

                                "& .MuiMenuItem-root.Mui-selected:hover": {
                                  backgroundColor: "#fd0000",
                                },
                              },
                            },
                          }}
                        >
                          {currencies.map((c) => (
                            <MenuItem key={c} value={c}>
                              {c}
                            </MenuItem>
                          ))}
                        </Select>

                        {/* Right: Amount Input */}
                        <TextField
                          variant="outlined"
                          placeholder="Receiving Amount"
                          value={formData.receiving_amount}
                          onChange={(e) =>
                            handleChange("receiving_amount", e.target.value)
                          }
                          fullWidth
                          inputProps={{
                            inputMode: "decimal",
                            style: {
                              padding: "0px",
                              fontWeight: 600,
                              fontSize: "13px",
                              textAlign: "right",
                            },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              border: "none",
                              "& fieldset": { border: "none" },
                              "&:hover fieldset": { border: "none" },
                              "&.Mui-focused fieldset": { border: "none" },
                            },
                            "& input::placeholder": {
                              color: "#7A7A7A",
                              opacity: 1,
                              fontSize: "13px",
                              fontWeight: 600,
                            },
                            pr: { xs: 0, md: 2 },
                          }}
                        />
                      </Stack>
                      {errors.receiving_amount && (
                        <Typography color="error" fontSize={12}>
                          {errors.receiving_amount}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  <Typography
                    sx={{
                      mt: 4,
                      textAlign: "center",
                      fontWeight: 700,
                      color: "#ffffff",
                    }}
                  >
                    Total Amount: {formData.total_amount}
                  </Typography>

                  <Box
                    sx={{ mt: 2, display: "flex", justifyContent: "center" }}
                  >
                    <CustomButton
                      variant="contained"
                      size="large"
                      type="submit"
                      sx={{
                        backgroundColor: "#fd0000",
                        color: "#fff",
                        width: "100%",
                        mx: { xs: 0, md: 6 },
                        p: "11px",
                        borderRadius: "20px",
                        textTransform: "none",
                        fontWeight: 700,
                        "&:hover": { backgroundColor: "#fd0000" },
                      }}
                    >
                      Book This Order
                    </CustomButton>
                  </Box>
                </Paper>
              </Stack>
            </form>
          </Grid>
        </Grid>
      </Container>
      {
        loginModalOpen && (
          <LoginQRModal open={loginModalOpen} handleClose={() => setLoginModalOpen(false)} />
        )
      }
    </Box >
  );
};

export default Conversation;
// import React, { useEffect, useMemo, useState, useRef } from "react";
// import {
//   Box,
//   Button,
//   CircularProgress,
//   Container,
//   Grid,
//   IconButton,
//   InputAdornment,
//   MenuItem,
//   Paper,
//   Select,
//   Stack,
//   TextField,
//   Typography,
// } from "@mui/material";
// import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
// import { CustomButton } from "../../utils/CustomTagComponents";
// import { useSelector } from "react-redux";
// import {
//   getForexExchangeData,
//   getForexServiceData,
//   submitForex,
// } from "../../services/ForexServicePageService";
// import LoginQRModal from "../../components/LoginQRModal ";

// const cardSx = {
//   borderRadius: "16px",
//   border: "0.5px solid #fd0000",
//   backgroundColor: "#fd0000",
//   p: 3,
// };

// const fieldSx = {
//   "& .MuiOutlinedInput-root": {
//     backgroundColor: "#fff",
//     borderRadius: "10px",
//   },
// };

// const smallSelectSx = {
//   minWidth: 84,
//   height: 40,
//   borderRadius: "10px",
//   backgroundColor: "#FF9500",
//   color: "#000",
//   "& .MuiSelect-icon": { color: "#000" },
//   "& fieldset": { border: "none" },
//   px: 1,
// };

// const Conversation = () => {
//   const { auth } = useSelector((state) => state.auth || {});
//   const isLoggedIn = Boolean(auth?.idToken || auth?.localId || auth?.email);

//   const { country_id, country_name } = useSelector((state) => state.auth || {});

//   const [formData, setFormData] = useState({
//     city: "", // "Select City" text field
//     transfer_from: "INR", // sendCur (e.g. "USD")
//     transfer_to: "USD", // recvCur (e.g. "INR")
//     forex_service: "", // service type (e.g. "Uni. Fee Transfer")
//     sending_amount: "1", // sendAmt
//     receiving_amount: "", // calculated or manually entered
//     total_amount: "", // totalAmount shown below
//   });
//   // Converter state

//   const [errors, setErrors] = useState({});

//   const [loginModalOpen, setLoginModalOpen] = useState(false);
//   const [currencies, setCurrencies] = useState([]);
//   const [liveRates, setLiveRates] = useState({});
//   const [liveRate, setLiveRate] = useState(null);
//   const [rateTimestamp, setRateTimestamp] = useState(null);
//   const [leftCur, setLeftCur] = useState("INR");
//   const [rightCur, setRightCur] = useState("USD");
//   const [leftAmt, setLeftAmt] = useState("1");
//   const [rightAmt, setRightAmt] = useState("");

//   const [loading, setLoading] = useState(true);
//   const [forexServiceData, setForexServiceData] = useState(null);
//   const [cityInput, setCityInput] = useState("");

//   useEffect(() => {
//     if (!country_id) return; // Wait for Redux country_id
//     setLoading(true);

//     const fetchFinanceSupportData = async () => {
//       try {
//         const res = await getForexServiceData(country_id);
//         if (res?.data?.success && res?.data?.data) {
//           setForexServiceData(res.data.data);
//         } else {
//           setForexServiceData([]);
//         }
//       } catch (err) {
//         console.error("❌ Error fetching Finance Support page:", err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchFinanceSupportData();
//   }, [country_id]);

//   useEffect(() => {
//     const fetchRates = async () => {
//       try {
//         const res = await getForexExchangeData();

//         if (res?.data?.success) {
//           setCurrencies(res.data?.currencies);
//           setLiveRates(res.data?.rateMap);
//           let rateMap = res.data?.rateMap || {};
//           setRateTimestamp(res.data?.timestamp || null);

//           // ✅ Default: 1 INR = USD
//           const defaultPair = "USDINR";
//           const defaultRate = rateMap[defaultPair] || null;
//           setLiveRate(defaultRate);

//           if (defaultRate) {
//             const usdAmt = (1 / defaultRate).toFixed(2);
//             setRightAmt(usdAmt);
//             setFormData((prev) => ({
//               ...prev,
//               receiving_amount: usdAmt,
//               total_amount: "1.00",
//             }));
//           }
//         } else {
//           setCurrencies([]);
//           setLiveRates({});
//           setRateTimestamp(null);
//         }
//       } catch (err) {
//         console.error("Error fetching IBR rates:", err);
//       }
//     };
//     fetchRates();
//   }, []);

//   // ✅ Update live rate when currencies change
//   useEffect(() => {
//     if (!liveRates || !leftCur || !rightCur) return;

//     if (leftCur === rightCur) {
//       setLiveRate(1);
//       return;
//     }

//     // Try to find direct or reverse pair (e.g. USDINR or INRUSD)
//     const directPair = `${leftCur}${rightCur}`;
//     const reversePair = `${rightCur}${leftCur}`;

//     let rate = null;

//     if (liveRates[directPair]) {
//       rate = liveRates[directPair];
//     } else if (liveRates[reversePair]) {
//       rate = 1 / liveRates[reversePair];
//     }

//     if (rate) setLiveRate(rate);
//   }, [leftCur, rightCur, liveRates]);

//   useEffect(() => {
//     if (!liveRate) return;

//     const leftValue = parseFloat(leftAmt);
//     const rightValue = parseFloat(rightAmt);

//     // If user typed in left side, sync right side
//     if (leftValue && !isNaN(leftValue)) {
//       setRightAmt((leftValue * liveRate).toFixed(2));
//     }
//     // Or if user typed in right side previously, sync left side
//     else if (rightValue && !isNaN(rightValue)) {
//       setLeftAmt((rightValue / liveRate).toFixed(2));
//     }
//   }, [liveRate, leftCur, rightCur]);

//   useEffect(() => {
//     if (
//       !liveRates ||
//       !formData?.transfer_from ||
//       !formData?.transfer_to ||
//       !formData?.sending_amount
//     )
//       return;

//     const from = formData.transfer_from;
//     const to = formData.transfer_to;
//     const sending = parseFloat(formData.sending_amount);

//     if (isNaN(sending)) return;

//     // const serviceChargeRate = 0.02; // 2% charge
//     const serviceChargeRate = 0;
//     let rate = null;

//     // Handle same currency
//     if (from === to) {
//       rate = 1;
//     } else {
//       const directPair = `${from}${to}`;
//       const reversePair = `${to}${from}`;

//       if (liveRates[directPair]) {
//         rate = parseFloat(liveRates[directPair]);
//       } else if (liveRates[reversePair]) {
//         rate = 1 / parseFloat(liveRates[reversePair]);
//       }
//     }

//     if (rate) {
//       // ✅ Step 1: Calculate receiving amount
//       const receiving = sending * rate;

//       // ✅ Step 2: Apply service charge on receiving amount
//       const serviceCharge = receiving * serviceChargeRate;

//       // ✅ Step 3: Deduct service charge to get total amount
//       const totalAfterCharge = receiving - serviceCharge;

//       setFormData((prev) => ({
//         ...prev,
//         rate: rate.toFixed(6),
//         receiving_amount: receiving.toFixed(2), // full received before charge
//         service_charge: serviceCharge.toFixed(2), // 2% of receiving
//         total_amount: totalAfterCharge.toFixed(2), // after deducting charge
//       }));
//     }
//   }, [
//     formData?.sending_amount,
//     formData?.transfer_from,
//     formData?.transfer_to,
//     liveRates,
//   ]);

//   // ✅ Conversion Handlers
//   const handleLeftAmt = (v) => {
//     setLeftAmt(v);
//     const n = parseFloat(v);
//     if (Number.isFinite(n) && liveRate) {
//       setRightAmt((n * liveRate).toFixed(2));
//     } else setRightAmt("");
//   };

//   const handleRightAmt = (v) => {
//     setRightAmt(v);
//     const n = parseFloat(v);
//     if (Number.isFinite(n) && liveRate) {
//       setLeftAmt((n / liveRate).toFixed(2));
//     } else setLeftAmt("");
//   };

//   const handleChange = (field, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: value,
//     }));
//   };

//   const validate = () => {
//     const newErrors = {};
//     if (!formData.city.trim()) newErrors.city = "City / Address is required";
//     if (!formData.transfer_from)
//       newErrors.transfer_from = "Select source currency";
//     if (!formData.transfer_to)
//       newErrors.transfer_to = "Select destination currency";
//     if (!formData.forex_service)
//       newErrors.forex_service = "Please choose a service";
//     if (!formData.sending_amount)
//       newErrors.sending_amount = "Enter sending amount";
//     if (!formData.receiving_amount)
//       newErrors.receiving_amount = "Enter receiving amount";
//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!isLoggedIn) {
//       setLoginModalOpen(true);
//       return;
//     }
//     if (!validate()) return;
//     try {
//       setLoading(true);
//       const payload = {
//         address: formData.city,
//         transfer_from_currency: formData.transfer_from,
//         transfer_to_currency: formData.transfer_to,
//         forex_service_id: formData.forex_service,
//         sending_amount: formData.sending_amount,
//         receving_amount: formData.receiving_amount,
//         rate: formData.rate,
//         total_amount: formData.total_amount,
//       };

//       const res = await submitForex(payload);

//       if (res?.data?.success) {
//         alert("✅ Order created successfully!");

//         // ✅ Reset form
//         setFormData({
//           city: "",
//           transfer_from: "INR",
//           transfer_to: "USD",
//           forex_service: "",
//           sending_amount: "1",
//           receiving_amount: "",
//           total_amount: "",
//         });
//         setCityInput("");
//         setLeftAmt("1");
//         setRightAmt(""); // reset converted amount
//       } else {
//         alert(res?.data?.message || "❌ Failed to create order.");
//       }
//     } catch (err) {
//       console.error("❌ Error creating order:", err);
//       alert("❌ Something went wrong. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//     console.log("✅ Submitted Data:", formData);
//   };

//   if (loading)
//     return (
//       <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
//         <CircularProgress className="frontsvg" />
//       </Box>
//     );

//   return (
//     <Box sx={{ py: { xs: 5, md: 8 } }}>
//       <Container>
//         <Grid container spacing={4}>
//           {/* Left: Converter */}
//           <Grid size={{ xs: 12, md: 6 }}>
//             <Paper
//               elevation={0}
//               sx={{
//                 ...cardSx,
//                 borderRadius: "20px",
//                 border: "0.5px solid #fd0000",
//                 backgroundColor: "#fd0000",
//                 p: { xs: "20px", md: "25px 25px 25px 35px" },
//               }}
//             >
//               <Typography
//                 sx={{
//                   fontWeight: 700,
//                   fontSize: { xs: 12, md: 15 },
//                   mb: "25px",
//                   color: "#ffffff",
//                 }}
//               >
//                 INR To EUR Currency Converter With Live Rate
//               </Typography>

//               <Paper
//                 variant="outlined"
//                 sx={{
//                   borderRadius: "20px",
//                   border: "none",
//                   backgroundColor: "#fd0000",
//                   p: { xs: "10px 15px", md: "11px 20px" },
//                 }}
//               >
//                 <Typography
//                   sx={{
//                     fontWeight: 700,
//                     fontSize: 11,
//                     color: "#ffffff",
//                     mb: 2,
//                     fontStyle: "bold",
//                   }}
//                 >
//                   EUR-INR Currency Converter
//                 </Typography>

//                 {/* Responsive input box layout */}
//                 <Box
//                   sx={{
//                     display: "flex",
//                     flexDirection: { xs: "column", sm: "row" },
//                     gap: 2,
//                     mt: 2,
//                   }}
//                 >
//                   {/* Left side */}
//                   <Stack
//                     direction="row"
//                     alignItems="center"
//                     sx={{
//                       backgroundColor: "#ffffff",
//                       border: "1.5px solid #ffffff",
//                       borderRadius: "4px",
//                       overflow: "hidden",
//                       width: "100%",
//                       p: "8px 11px",
//                       height: "63px",
//                     }}
//                   >
//                     <TextField
//                       variant="outlined"
//                       placeholder={`Enter ${leftCur} Amount`}
//                       value={leftAmt}
//                       onChange={(e) => handleLeftAmt(e.target.value)}
//                       fullWidth
//                       inputProps={{
//                         inputMode: "decimal",
//                         style: {
//                           padding: "0px",
//                           fontWeight: 600,
//                           fontSize: "13px",
//                           height: "41px",
//                         },
//                       }}
//                       sx={{
//                         "& .MuiOutlinedInput-root": {
//                           border: "none",
//                           "& fieldset": { border: "none" },
//                           "&:hover fieldset": { border: "none" },
//                           "&.Mui-focused fieldset": { border: "none" },
//                         },
//                         "& input::placeholder": {
//                           color: "#999",
//                           opacity: 1,
//                           fontSize: "13px",
//                           fontWeight: 500,
//                         },
//                       }}
//                     />

//                     <Select
//                       value={leftCur}
//                       onChange={(e) => setLeftCur(e.target.value)}
//                       IconComponent={KeyboardArrowDownIcon}
//                       sx={{
//                         backgroundColor: "#fd0000",
//                         fontWeight: 700,
//                         fontSize: "11px",
//                         p: "5px 10px",
//                         height: "100%",
//                         minWidth: "63px",
//                         maxWidth: "63px",
//                         borderRadius: "2px",
//                         borderLeft: "1.5px solid #fd0000",

//                         /* Root text */
//                         color: "#ffffff",

//                         /* Selected value */
//                         "& .MuiSelect-select": {
//                           display: "flex",
//                           alignItems: "center",
//                           padding: "0px 28px 0px 0px !important",
//                           color: "#ffffff !important",
//                         },

//                         /* Arrow */
//                         "& .MuiSelect-icon": {
//                           color: "#ffffff",
//                           right: 8,
//                         },

//                         /* Remove border */
//                         "& .MuiOutlinedInput-notchedOutline": {
//                           border: "none",
//                         },
//                       }}
//                       MenuProps={{
//                         PaperProps: {
//                           sx: {
//                             bgcolor: "#ffffff",

//                             /* All items */
//                             "& .MuiMenuItem-root": {
//                               color: "#002045",
//                               fontWeight: 600,
//                               fontSize: "11px",
//                             },

//                             /* Hover */
//                             "& .MuiMenuItem-root:hover": {
//                               backgroundColor: "rgba(255,255,255,0.2)",
//                             },

//                             /* Selected item */
//                             "& .MuiMenuItem-root.Mui-selected": {
//                               backgroundColor: "#fd0000",
//                               color: "#ffffff",
//                             },

//                             "& .MuiMenuItem-root.Mui-selected:hover": {
//                               backgroundColor: "#fd0000",
//                             },
//                           },
//                         },
//                       }}
//                     >
//                       {currencies.map((c) => (
//                         <MenuItem key={c} value={c}>
//                           {c}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </Stack>

//                   {/* Right side */}
//                   <Stack
//                     direction="row"
//                     alignItems="center"
//                     sx={{
//                       backgroundColor: "#ffffff",
//                       border: "1.5px solid #ffffff",
//                       borderRadius: "4px",
//                       overflow: "hidden",
//                       width: "100%",
//                       p: "8px 11px",
//                       height: "63px",
//                     }}
//                   >
//                     <TextField
//                       variant="outlined"
//                       placeholder={`Enter ${rightCur} Amount`}
//                       value={rightAmt}
//                       onChange={(e) => handleRightAmt(e.target.value)}
//                       fullWidth
//                       inputProps={{
//                         inputMode: "decimal",
//                         style: {
//                           padding: "0px",
//                           fontWeight: 700,
//                           fontSize: "13px",
//                           height: "41px",
//                         },
//                       }}
//                       sx={{
//                         "& .MuiOutlinedInput-root": {
//                           border: "none",
//                           "& fieldset": { border: "none" },
//                           "&:hover fieldset": { border: "none" },
//                           "&.Mui-focused fieldset": { border: "none" },
//                         },
//                         "& input::placeholder": {
//                           color: "#999",
//                           opacity: 1,
//                           fontSize: "13px",
//                           fontWeight: 500,
//                         },
//                       }}
//                     />

//                     <Select
//                       value={rightCur}
//                       onChange={(e) => setRightCur(e.target.value)}
//                       IconComponent={KeyboardArrowDownIcon}
//                       sx={{
//                         backgroundColor: "#fd0000",
//                         fontWeight: 700,
//                         fontSize: "11px",
//                         p: "5px 10px",
//                         height: "100%",
//                         minWidth: "63px",
//                         maxWidth: "63px",
//                         borderRadius: "2px",
//                         borderLeft: "1.5px solid #fd0000",

//                         /* Root text */
//                         color: "#ffffff",

//                         /* Selected value */
//                         "& .MuiSelect-select": {
//                           display: "flex",
//                           alignItems: "center",
//                           padding: "0px 28px 0px 0px !important",
//                           color: "#ffffff !important",
//                         },

//                         /* Arrow */
//                         "& .MuiSelect-icon": {
//                           color: "#ffffff",
//                           right: 8,
//                         },

//                         /* Remove border */
//                         "& .MuiOutlinedInput-notchedOutline": {
//                           border: "none",
//                         },
//                       }}
//                       MenuProps={{
//                         PaperProps: {
//                           sx: {
//                             bgcolor: "#ffffff",

//                             /* All items */
//                             "& .MuiMenuItem-root": {
//                               color: "#002045",
//                               fontWeight: 600,
//                               fontSize: "11px",
//                             },

//                             /* Hover */
//                             "& .MuiMenuItem-root:hover": {
//                               backgroundColor: "rgba(255,255,255,0.2)",
//                             },

//                             /* Selected item */
//                             "& .MuiMenuItem-root.Mui-selected": {
//                               backgroundColor: "#fd0000",
//                               color: "#ffffff",
//                             },

//                             "& .MuiMenuItem-root.Mui-selected:hover": {
//                               backgroundColor: "#fd0000",
//                             },
//                           },
//                         },
//                       }}
//                     >
//                       {currencies.map((c) => (
//                         <MenuItem key={c} value={c}>
//                           {c}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </Stack>
//                 </Box>

//                 {/* Rate Info Section */}
//                 <Stack
//                   direction="column"
//                   alignItems="center"
//                   justifyContent="center"
//                   sx={{
//                     mt: { xs: "30px", md: "46px" },
//                     gap: "8px",
//                     textAlign: "center",
//                   }}
//                 >
//                   <Stack
//                     direction="row"
//                     alignItems="center"
//                     justifyContent="center"
//                     sx={{
//                       gap: "11px",
//                       flexWrap: "wrap",
//                     }}
//                   >
//                     <Box
//                       sx={{
//                         width: 13,
//                         height: 13,
//                         borderRadius: "50%",
//                         bgcolor: "#15FF00",
//                       }}
//                     />
//                     <Typography
//                       sx={{ fontWeight: 700, fontSize: { xs: 15, md: 18, color: "#ffffff" } }}
//                     >
//                       1 {leftCur} = {liveRate ? liveRate.toFixed(4) : "--"}{" "}
//                       {rightCur}
//                     </Typography>
//                   </Stack>

//                   <Typography
//                     sx={{
//                       fontSize: { xs: 11, md: 12 },
//                       color: "#ffffff",
//                       letterSpacing: 0.3,
//                     }}
//                   >
//                     Interbank/ Mid-Market Rate At: {rateTimestamp}
//                   </Typography>
//                 </Stack>
//               </Paper>
//             </Paper>
//           </Grid>

//           {/* Right: Booking form */}
//           <Grid size={{ xs: 12, md: 6 }}>
//             <form onSubmit={handleSubmit} noValidate>
//               <Stack
//                 direction="column"
//                 sx={{
//                   gap: "10px",
//                 }}
//               >
//                 <Stack direction="column">
//                   <Paper
//                     elevation={0}
//                     sx={{ ...cardSx, p: "0px", borderRadius: "10px" }}
//                   >
//                     <TextField
//                       placeholder="Enter City / Address"
//                       fullWidth
//                       variant="outlined"
//                       value={cityInput}
//                       onChange={(e) => {
//                         const value = e.target.value;
//                         setCityInput(value);
//                         setFormData((prev) => ({
//                           ...prev,
//                           city: value,
//                         }));
//                       }}
//                       sx={{
//                         "& .MuiOutlinedInput-root": {
//                           color: "#002045",
//                           backgroundColor: "#ffffff",
//                           borderRadius: "10px",
//                           fontSize: "13px",
//                           fontWeight: 700,
//                           height: "41px",
//                           "& fieldset": { border: "none" },
//                           "&:hover fieldset": { border: "none" },
//                           "&.Mui-focused fieldset": { border: "none" },
//                         },
//                         "& .MuiInputBase-input::placeholder": {
//                           fontWeight: 600,
//                           fontSize: "13px",
//                           color: "#002045",
//                           opacity: 1,
//                         },
//                       }}
//                     />
//                   </Paper>
//                   {errors.city && (
//                     <Typography color="error" fontSize={12}>
//                       {errors.city}
//                     </Typography>
//                   )}
//                 </Stack>

//                 <Paper
//                   variant="outlined"
//                   sx={{
//                     ...cardSx,
//                     borderRadius: "20px",
//                     p: "17px 17px 41px 17px",
//                   }}
//                 >
//                   <Grid container spacing={2}>
//                     <Grid size={{ xs: 12, md: 6 }}>
//                       <Typography
//                         sx={{
//                           mb: 1,
//                           fontSize: 13,
//                           fontWeight: 700,
//                           fontStyle: "bold",
//                         }}
//                       >
//                         Transfer From
//                       </Typography>
//                       <Select
//                         fullWidth
//                         value={formData.transfer_from}
//                         onChange={(e) =>
//                           handleChange("transfer_from", e.target.value)
//                         }
//                         IconComponent={KeyboardArrowDownIcon}
//                         sx={{
//                           ...fieldSx,
//                           "& .MuiSelect-select": {
//                             padding: "0 !important", // ✅ removes default internal padding
//                             display: "flex",
//                             alignItems: "center",
//                           },
//                           "& fieldset": {
//                             border: "1px solid #ffffff", // default border
//                           },
//                           "&:hover fieldset": {
//                             border: "1px solid #ffffff", // keep orange on hover
//                           },
//                           "&.Mui-focused fieldset": {
//                             border: "1px solid #ffffff", // keep orange on focus
//                           },
//                           p: "5px 16px",
//                           height: "32px",
//                           borderRadius: "4px",
//                           background: "#ffffff",
//                           color: "#002045"
//                         }}
//                         MenuProps={{
//                           PaperProps: {
//                             sx: {
//                               bgcolor: "#ffffff",

//                               /* All items */
//                               "& .MuiMenuItem-root": {
//                                 color: "#002045",
//                                 fontWeight: 600,
//                                 fontSize: "11px",
//                               },

//                               /* Hover */
//                               "& .MuiMenuItem-root:hover": {
//                                 backgroundColor: "rgba(255,255,255,0.2)",
//                               },

//                               /* Selected item */
//                               "& .MuiMenuItem-root.Mui-selected": {
//                                 backgroundColor: "#fd0000",
//                                 color: "#ffffff",
//                               },

//                               "& .MuiMenuItem-root.Mui-selected:hover": {
//                                 backgroundColor: "#fd0000",
//                               },
//                             },
//                           },
//                         }}
//                       >
//                         {currencies.map((c) => (
//                           <MenuItem key={c} value={c}>
//                             {c}
//                           </MenuItem>
//                         ))}
//                       </Select>
//                       {errors.transfer_from && (
//                         <Typography color="error" fontSize={12}>
//                           {errors.transfer_from}
//                         </Typography>
//                       )}
//                     </Grid>
//                     <Grid size={{ xs: 12, md: 6 }}>
//                       <Typography
//                         sx={{
//                           mb: 1,
//                           fontSize: 13,
//                           fontWeight: 700,
//                           fontStyle: "bold",
//                         }}
//                       >
//                         Transfer To
//                       </Typography>
//                       <Select
//                         fullWidth
//                         value={formData.transfer_to}
//                         onChange={(e) =>
//                           handleChange("transfer_to", e.target.value)
//                         }
//                         IconComponent={KeyboardArrowDownIcon}
//                         sx={{
//                           ...fieldSx,
//                           "& .MuiSelect-select": {
//                             padding: "0 !important", // ✅ removes default internal padding
//                             display: "flex",
//                             alignItems: "center",
//                           },
//                           "& fieldset": {
//                             border: "1px solid #ffffff", // default border
//                           },
//                           "&:hover fieldset": {
//                             border: "1px solid #ffffff", // keep orange on hover
//                           },
//                           "&.Mui-focused fieldset": {
//                             border: "1px solid #ffffff", // keep orange on focus
//                           },
//                           p: "5px 16px",
//                           height: "32px",
//                           borderRadius: "4px",
//                           background: "#ffffff",
//                           color: "#002045"
//                         }}
//                         MenuProps={{
//                           PaperProps: {
//                             sx: {
//                               bgcolor: "#ffffff",

//                               /* All items */
//                               "& .MuiMenuItem-root": {
//                                 color: "#002045",
//                                 fontWeight: 600,
//                                 fontSize: "11px",
//                               },

//                               /* Hover */
//                               "& .MuiMenuItem-root:hover": {
//                                 backgroundColor: "rgba(255,255,255,0.2)",
//                               },

//                               /* Selected item */
//                               "& .MuiMenuItem-root.Mui-selected": {
//                                 backgroundColor: "#fd0000",
//                                 color: "#ffffff",
//                               },

//                               "& .MuiMenuItem-root.Mui-selected:hover": {
//                                 backgroundColor: "#fd0000",
//                               },
//                             },
//                           },
//                         }}
//                       >
//                         {currencies.map((c) => (
//                           <MenuItem key={c} value={c}>
//                             {c}
//                           </MenuItem>
//                         ))}
//                       </Select>
//                       {errors.transfer_to && (
//                         <Typography color="error" fontSize={12}>
//                           {errors.transfer_to}
//                         </Typography>
//                       )}
//                     </Grid>

//                     <Grid size={{ xs: 12 }}>
//                       <Typography
//                         sx={{
//                           mb: 1,
//                           fontSize: 13,
//                           fontWeight: 700,
//                           fontStyle: "bold",
//                         }}
//                       >
//                         Forex Service
//                       </Typography>
//                       <Select
//                         fullWidth
//                         value={formData.forex_service}
//                         onChange={(e) =>
//                           handleChange("forex_service", e.target.value)
//                         }
//                         IconComponent={KeyboardArrowDownIcon}
//                         sx={{
//                           ...fieldSx,
//                           "& .MuiSelect-select": {
//                             padding: "0 !important", // ✅ removes default internal padding
//                             display: "flex",
//                             alignItems: "center",
//                           },
//                           "& fieldset": {
//                             border: "1px solid #ffffff", // default border
//                           },
//                           "&:hover fieldset": {
//                             border: "1px solid #ffffff", // keep orange on hover
//                           },
//                           "&.Mui-focused fieldset": {
//                             border: "1px solid #ffffff", // keep orange on focus
//                           },
//                           p: "5px 16px",
//                           height: "32px",
//                           borderRadius: "4px",
//                           background: "#ffffff",
//                           color: "#002045"
//                         }}
//                         MenuProps={{
//                           PaperProps: {
//                             sx: {
//                               bgcolor: "#ffffff",

//                               /* All items */
//                               "& .MuiMenuItem-root": {
//                                 color: "#002045",
//                                 fontWeight: 600,
//                                 fontSize: "11px",
//                               },

//                               /* Hover */
//                               "& .MuiMenuItem-root:hover": {
//                                 backgroundColor: "rgba(255,255,255,0.2)",
//                               },

//                               /* Selected item */
//                               "& .MuiMenuItem-root.Mui-selected": {
//                                 backgroundColor: "#fd0000",
//                                 color: "#ffffff",
//                               },

//                               "& .MuiMenuItem-root.Mui-selected:hover": {
//                                 backgroundColor: "#fd0000",
//                               },
//                             },
//                           },
//                         }}
//                       >
//                         {forexServiceData?.length > 0 &&
//                           forexServiceData.map((service, index) => (
//                             <MenuItem value={service.id} key={index}>
//                               {service.name}
//                             </MenuItem>
//                           ))}
//                       </Select>
//                       {errors.forex_service && (
//                         <Typography color="error" fontSize={12}>
//                           {errors.forex_service}
//                         </Typography>
//                       )}
//                     </Grid>

//                     <Grid size={{ xs: 12 }}>
//                       <Typography
//                         sx={{
//                           mb: 1,
//                           fontSize: 13,
//                           fontWeight: 700,
//                           fontStyle: "bold",
//                         }}
//                       >
//                         Sending Amount
//                       </Typography>
//                       <Stack
//                         direction="row"
//                         alignItems="center"
//                         sx={{
//                           backgroundColor: "#FFF4E6",
//                           border: "1px solid #FF9500",
//                           borderRadius: "4px",
//                           overflow: "hidden",
//                           p: "8px 11px",
//                           width: "100%",
//                           height: "41px",
//                           transition: "border-color 0.3s",
//                           "&:hover": { borderColor: "#FF9500" },
//                           "&:focus-within": { borderColor: "#FF9500" },
//                         }}
//                       >
//                         {/* Left: Currency Select */}
//                         <Select
//                           value={formData.transfer_from}
//                           onChange={(e) =>
//                             handleChange("transfer_from", e.target.value)
//                           }
//                           IconComponent={KeyboardArrowDownIcon}
//                           sx={{
//                             backgroundColor: "#FF9500",
//                             color: "#fff",
//                             fontWeight: 600,
//                             fontSize: "13px",
//                             px: 2,
//                             height: "100%",
//                             borderRadius: "0px",
//                             display: "flex",
//                             alignItems: "center",
//                             minWidth: { xs: "90px", sm: "118px" },
//                             maxWidth: { xs: "90px", sm: "118px" },
//                             "& .MuiSelect-select": {
//                               padding: "0px 24px 0px 0px !important",
//                               display: "flex",
//                               alignItems: "center",
//                             },
//                             "& .MuiSelect-icon": {
//                               color: "#fff",
//                               right: 8,
//                             },
//                             "& fieldset": { border: "none" },
//                             "&:hover fieldset": { border: "none" },
//                             "&.Mui-focused fieldset": { border: "none" },
//                           }}
//                           MenuProps={{
//                             PaperProps: {
//                               sx: {
//                                 bgcolor: "#ffffff",

//                                 /* All items */
//                                 "& .MuiMenuItem-root": {
//                                   color: "#002045",
//                                   fontWeight: 600,
//                                   fontSize: "11px",
//                                 },

//                                 /* Hover */
//                                 "& .MuiMenuItem-root:hover": {
//                                   backgroundColor: "rgba(255,255,255,0.2)",
//                                 },

//                                 /* Selected item */
//                                 "& .MuiMenuItem-root.Mui-selected": {
//                                   backgroundColor: "#fd0000",
//                                   color: "#ffffff",
//                                 },

//                                 "& .MuiMenuItem-root.Mui-selected:hover": {
//                                   backgroundColor: "#fd0000",
//                                 },
//                               },
//                             },
//                           }}
//                         >
//                           {currencies.map((c) => (
//                             <MenuItem key={c} value={c}>
//                               {c}
//                             </MenuItem>
//                           ))}
//                         </Select>

//                         {/* Right: Amount Input */}
//                         <TextField
//                           variant="outlined"
//                           placeholder="Sending Amount"
//                           value={formData.sending_amount}
//                           onChange={(e) =>
//                             handleChange("sending_amount", e.target.value)
//                           }
//                           fullWidth
//                           inputProps={{
//                             inputMode: "decimal",
//                             style: {
//                               padding: "0px",
//                               fontWeight: 600,
//                               fontSize: "13px",
//                               textAlign: "right",
//                             },
//                           }}
//                           sx={{
//                             "& .MuiOutlinedInput-root": {
//                               border: "none",
//                               "& fieldset": { border: "none" },
//                               "&:hover fieldset": { border: "none" },
//                               "&.Mui-focused fieldset": { border: "none" },
//                             },
//                             "& input::placeholder": {
//                               color: "#7A7A7A",
//                               opacity: 1,
//                               fontSize: "13px",
//                               fontWeight: 600,
//                             },
//                             pr: { xs: 0, md: 2 },
//                           }}
//                         />
//                       </Stack>
//                       {errors.sending_amount && (
//                         <Typography color="error" fontSize={12}>
//                           {errors.sending_amount}
//                         </Typography>
//                       )}
//                     </Grid>

//                     {/* Receiving Amount */}
//                     <Grid size={{ xs: 12 }}>
//                       <Typography
//                         sx={{
//                           mb: 1,
//                           fontSize: 13,
//                           fontWeight: 700,
//                           fontStyle: "bold",
//                         }}
//                       >
//                         Receiving Amount
//                       </Typography>
//                       <Stack
//                         direction="row"
//                         alignItems="center"
//                         sx={{
//                           backgroundColor: "#FFF4E6",
//                           border: "1px solid #FF9500",
//                           borderRadius: "4px",
//                           overflow: "hidden",
//                           p: "8px 11px",
//                           width: "100%",
//                           height: "41px",
//                           transition: "border-color 0.3s",
//                           "&:hover": { borderColor: "#FF9500" },
//                           "&:focus-within": { borderColor: "#FF9500" },
//                         }}
//                       >
//                         {/* Left: Currency Select */}
//                         <Select
//                           value={formData.transfer_to}
//                           onChange={(e) =>
//                             handleChange("transfer_to", e.target.value)
//                           }
//                           IconComponent={KeyboardArrowDownIcon}
//                           sx={{
//                             backgroundColor: "#FF9500",
//                             color: "#fff",
//                             fontWeight: 600,
//                             fontSize: "13px",
//                             px: 2,
//                             height: "100%",
//                             borderRadius: "0px",
//                             display: "flex",
//                             alignItems: "center",
//                             minWidth: { xs: "90px", sm: "118px" },
//                             maxWidth: { xs: "90px", sm: "118px" },
//                             "& .MuiSelect-select": {
//                               padding: "0px 24px 0px 0px !important",
//                               display: "flex",
//                               alignItems: "center",
//                             },
//                             "& .MuiSelect-icon": {
//                               color: "#fff",
//                               right: 8,
//                             },
//                             "& fieldset": { border: "none" },
//                             "&:hover fieldset": { border: "none" },
//                             "&.Mui-focused fieldset": { border: "none" },
//                           }}
//                           MenuProps={{
//                             PaperProps: {
//                               sx: {
//                                 bgcolor: "#ffffff",

//                                 /* All items */
//                                 "& .MuiMenuItem-root": {
//                                   color: "#002045",
//                                   fontWeight: 600,
//                                   fontSize: "11px",
//                                 },

//                                 /* Hover */
//                                 "& .MuiMenuItem-root:hover": {
//                                   backgroundColor: "rgba(255,255,255,0.2)",
//                                 },

//                                 /* Selected item */
//                                 "& .MuiMenuItem-root.Mui-selected": {
//                                   backgroundColor: "#fd0000",
//                                   color: "#ffffff",
//                                 },

//                                 "& .MuiMenuItem-root.Mui-selected:hover": {
//                                   backgroundColor: "#fd0000",
//                                 },
//                               },
//                             },
//                           }}
//                         >
//                           {currencies.map((c) => (
//                             <MenuItem key={c} value={c}>
//                               {c}
//                             </MenuItem>
//                           ))}
//                         </Select>

//                         {/* Right: Amount Input */}
//                         <TextField
//                           variant="outlined"
//                           placeholder="Receiving Amount"
//                           value={formData.receiving_amount}
//                           onChange={(e) =>
//                             handleChange("receiving_amount", e.target.value)
//                           }
//                           fullWidth
//                           inputProps={{
//                             inputMode: "decimal",
//                             style: {
//                               padding: "0px",
//                               fontWeight: 600,
//                               fontSize: "13px",
//                               textAlign: "right",
//                             },
//                           }}
//                           sx={{
//                             "& .MuiOutlinedInput-root": {
//                               border: "none",
//                               "& fieldset": { border: "none" },
//                               "&:hover fieldset": { border: "none" },
//                               "&.Mui-focused fieldset": { border: "none" },
//                             },
//                             "& input::placeholder": {
//                               color: "#7A7A7A",
//                               opacity: 1,
//                               fontSize: "13px",
//                               fontWeight: 600,
//                             },
//                             pr: { xs: 0, md: 2 },
//                           }}
//                         />
//                       </Stack>
//                       {errors.receiving_amount && (
//                         <Typography color="error" fontSize={12}>
//                           {errors.receiving_amount}
//                         </Typography>
//                       )}
//                     </Grid>
//                   </Grid>

//                   <Typography
//                     sx={{
//                       mt: 4,
//                       textAlign: "center",
//                       fontWeight: 700,
//                       color: "#ffffff",
//                     }}
//                   >
//                     Total Amount: {formData.total_amount}
//                   </Typography>

//                   <Box
//                     sx={{ mt: 2, display: "flex", justifyContent: "center" }}
//                   >
//                     <CustomButton
//                       variant="contained"
//                       size="large"
//                       type="submit"
//                       sx={{
//                         backgroundColor: "#fd0000",
//                         color: "#fff",
//                         width: "100%",
//                         mx: { xs: 0, md: 6 },
//                         p: "11px",
//                         borderRadius: "4px",
//                         textTransform: "none",
//                         fontWeight: 700,
//                         "&:hover": { backgroundColor: "#fd0000" },
//                       }}
//                     >
//                       Book This Order
//                     </CustomButton>
//                   </Box>
//                 </Paper>
//               </Stack>
//             </form>
//           </Grid>
//         </Grid>
//       </Container>
//       {loginModalOpen && (
//         <LoginQRModal open={loginModalOpen} handleClose={() => setLoginModalOpen(false)} />
//       )}
//     </Box>
//   );
// };

// export default Conversation;
